<script lang="ts">
  import { onMount } from 'svelte';
  import {
    getJoodMembershipBasedPropertyValue,
    isMobile,
    isValueSponsoredOrExtraSponsored,
  } from '../../../../common/util';
  import { brandGtmBannerClickEvent, gtmBannerViewEvent } from '../../../brand/services/brand-data-services';
  import SponsoredTag from '../../components/SponsoredTag.svelte';
  import viewport from '../../components/useViewportAction';
  import { getDataFromAmplienceAsync, parseImageBannerData } from '../../services/amplience';

  import { getAppConfig } from '../../../../common/config/app-config';
  import { addHomeMaintenanceToCart } from '../../../home-maintenance/service/home-maintenance-data-services';
  import Text from '../../components/Text.svelte';
  import ImageTag from '../ImageTag.svelte';
  import PictureTag from '../PictureTag.svelte';
  export let metaData;
  export let isDynamicRender;
  export let initialHeightForCLS = isMobile() ? '0' : metaData?.preorder ? '230px' : '300px';
  let isInViewport = false;

  const { user, themeResourcePath, homeMaintenanceCustomerVipEndDate, homeMaintenanceVipMembershipActive, pageId } =
    getAppConfig();

  let starIcon = `${themeResourcePath}/images/homeservices/vipstar.svg`;

  const getBannerDetails = () => {
    const amplienceId = getJoodMembershipBasedPropertyValue(
      metaData,
      'amplienceId',
      'blueAmplienceId',
      'goldAmplienceId'
    );
    return getDataFromAmplienceAsync(amplienceId)
      .then(res => parseImageBannerData(res as any))
      .then(imageBannerData => {
        image = imageBannerData.image;
        mobileImage = imageBannerData.mobileImage;
        link = imageBannerData.link;
        bannerName = imageBannerData.bannerName;
        bannerId = imageBannerData.bannerId;
      })
      .then(() => {
        loaded = true;
        if (isSponsored) {
          return { className: 'divider-sponsored' };
        }
        return loaded;
      })
      .catch(() => (loaded = false));
  };

  onMount(async () => {
    if (isDynamicRender == 'false') {
      getBannerDetails();
    }
  });

  export function onLoad() {
    getBannerDetails();
  }

  function handleBannerClick() {
    brandGtmBannerClickEvent(bannerId, bannerName, brandName, isSponsored);

    if (homeMaintenanceVipMembershipActive) {
      return;
    }

    if (isHomeMaintenanceVip) {
      addHomeMaintenanceToCart({ vipAddToCart: true, source: 'Services Home page' });
    } else {
      // manual redirect for normal banners
      window.location.href = link;
    }
  }

  let loaded: boolean = false;
  let image: string;
  let mobileImage: string;
  let link: string;
  let bannerName: string;
  let bannerId: string;

  $: isSponsored = metaData && isValueSponsoredOrExtraSponsored(metaData?.sponsoredType);
  $: brandName = metaData?.brandCode;
  $: isHomeMaintenanceVip =
    metaData?.slotName === 'HomeMaintanenceVipTopBannerSlot' && pageId === 'extraHomeMaintenancePage';
</script>

<section
  class="image-banner-container {isHomeMaintenanceVip ? 'hm-become-vip-banner' : 'hm-vip-banner'}"
  style:min-height={initialHeightForCLS}
>
  <a
    href={link}
    on:click|preventDefault={handleBannerClick}
    on:click
    use:viewport
    on:enterViewport={() => {
      isInViewport = true;
      gtmBannerViewEvent(bannerId, bannerName, brandName, isSponsored);
    }}
  >
    {#if metaData?.preorder}
      <PictureTag on:load={() => (loaded = true)} {image} {mobileImage} width="100%" />
    {:else}
      <ImageTag imageUrls={[mobileImage, image]} widths={[430, 1440]} heights={[430, 300]} alt="1x1 banner" />
    {/if}
    {#if homeMaintenanceVipMembershipActive && metaData?.slotName === 'HomeMaintanenceVipTopBannerSlot' && loaded && isInViewport}
      <!-- TOP LEFT GREETING -->
      <div class="banner-greeting">
        <img src={starIcon} alt="star" class="star" />
        <Text key="extra.home.service.vip.user.name" args={[user?.firstName]} />
        <img src={starIcon} alt="star" class="star" />
      </div>

      <!-- BOTTOM RIGHT VALIDITY -->
      <div class="banner-validity">
        <Text key="extra.home.service.vip.valid.until" args={[homeMaintenanceCustomerVipEndDate]} />
      </div>
    {/if}
  </a>
  {#if isSponsored}
    <SponsoredTag variant="banner" />
  {/if}
</section>
{#if !loaded && !metaData?.preorder}
  <div class="skeleton-item"></div>
{/if}

<style lang="scss">
  @import '../../../common/styles/colors.new.scss';

  .skeleton-item {
    height: 300px;
  }
  .image-banner-container {
    position: relative;

    &.hm-become-vip-banner {
      max-width: 1266px;
      margin: 0 auto;
      global {
        img {
          border-radius: 20px;
        }
      }
    }

    &.hm-vip-banner {
      max-width: 1322px;
      margin: 0 auto;
    }

    .banner-greeting {
      position: absolute;
      top: 86px;
      left: 38px;
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 36px;
      font-weight: 700;
      line-height: 33px;
      background: linear-gradient(
        90deg,
        #bc9a4d 0%,
        #b99633 17.5%,
        #c7a235 26.25%,
        #cda936 30.63%,
        #d1ac37 32.81%,
        #d2ad37 33.91%,
        #d4af37 35%,
        #f5e6a8 55%,
        #c6a75e 80%,
        #9e7c2f 100%
      );

      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      color: transparent;

      @include desktop-max-width {
        font-size: 23px;
        bottom: 160px;
        left: 24px;
      }
    }

    .star {
      width: 27px;
      height: 27px;
      @include desktop-max-width {
        width: 15px;
        height: 15px;
      }
    }

    .banner-validity {
      position: absolute;
      bottom: 18px;
      right: 18px;
      color: $primary-white;
      font-size: 16px;
      font-weight: 400;
      line-height: 33px;

      @include desktop-max-width {
        font-size: 12px;
        bottom: 8px;
        right: 16px;
      }
    }
  }
</style>
